import { useToast } from "@/components/ui/use-toast";
import { useCreateAuthorizePaymentMutation } from "@/redux/apiSlices/paymentApi";
import { setUserCart } from "@/redux/slices/userCartSlice";
import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import { useAcceptJs } from "react-acceptjs";
import { useDispatch, useSelector } from "react-redux";
import { getOrCreateSignifydSessionId } from "@/lib/auth";
import { handlePaymentRedirect } from "@/lib/paymentUtils";

const authData = {
  apiLoginID: process.env.NEXT_PUBLIC_API_LOGIN_ID,
  clientKey: process.env.NEXT_PUBLIC_CLIENT_KEY,
};


const useAuthorizePayment = (orderId, setIsProcessing) => {
  const { isLogin } = useSelector((state) => state.auth);
  const hasHandledSuccess = useRef(false);
  const { toast } = useToast();
  const dispatch = useDispatch();
  const router = useRouter();
  const { dispatchData } = useAcceptJs({
    environment: process.env.NEXT_PUBLIC_AUTHORIZE_ENVIROMENT,
    authData,
  });

  const cardData = useSelector((state) => state.authorizeDotNetCardDetails);

  const [
    generateAuthorizeToken,
    {
      data: authorizePaymentData,
      isSuccess: isAuthorizeSuccess,
      isLoading: isAuthorizeLoading,
      isError: isAuthorizeError,
      error: authorizeError,
    },
  ] = useCreateAuthorizePaymentMutation();

  useEffect(() => {
    const handleAuthorizeSuccess = async () => {
      if (authorizePaymentData?.success) {
        toast({
          title: authorizePaymentData?.msg || "Payment processed successfully!",
        });

        if (orderId) {
          dispatch(setUserCart({}));
          setIsProcessing(false); // 🔄 Start loader
          handlePaymentRedirect({ isLogin, orderId, router, dispatch, toast });
        }
      } else {
        toast({
          variant: "destructive",
          title: "Payment failed",
          description:
            authorizePaymentData?.msg || "An unknown error occurred.",
        });
        setIsProcessing(false); // 🔄 Start loader
      }
    };

    if (
      !isAuthorizeLoading &&
      isAuthorizeSuccess &&
      authorizePaymentData &&
      !hasHandledSuccess.current
    ) {
      hasHandledSuccess.current = true; // ✅ Prevent multiple calls
      handleAuthorizeSuccess();
    }
  }, [
    isAuthorizeSuccess,
    authorizePaymentData,
    isAuthorizeLoading,
    orderId,
    dispatch,
    router,
    toast,
  ]);

  useEffect(() => {
    if (!isAuthorizeLoading && isAuthorizeError && authorizeError) {
      const errorMessage =
        authorizeError?.data?.msg ||
        authorizeError?.message ||
        "An error occurred during payment processing.";

      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });

      setIsProcessing(false);
    }
  }, [
    isAuthorizeError,
    authorizeError,
    isAuthorizeLoading,
    toast,
    setIsProcessing,
  ]);

  const authorizePaymentHandler = async (orderId) => {
    try {
      setIsProcessing(true); // 🔄 Start loader
      const response = await dispatchData({
        cardData: {
          cardNumber: cardData.cardNumber,
          month: cardData.month,
          year: cardData.year,
          cardCode: cardData.cardCode,
        },
      });
      if (response?.messages?.resultCode === "Ok") {
        const token = response?.opaqueData?.dataValue;
      const res = await fetch("/api/get-ip");
      const { ip } = await res.json();
      const sessionId = getOrCreateSignifydSessionId();

      const obj = {
        paymentToken: token,
        orderId: orderId,
        clientIp: ip,
        sessionId,
      };

      generateAuthorizeToken(obj);
      } else {
        toast({
          variant: "destructive",
          title:
            response?.messages?.message?.[0]?.text ||
            "An unexpected error occurred",
        });
        setIsProcessing(false);
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title:
          error?.messages?.message?.[0]?.text ||
          "An unexpected error occurred. Please try again.",
      });
      setIsProcessing(false);
    }
  };
  return { authorizePaymentHandler };
  // return { authorizePaymentHandler, isAuthorizeLoading };
};

export default useAuthorizePayment;
